import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserSlash } from '@fortawesome/pro-duotone-svg-icons/faUserSlash'

import Avatar from '../../../../Components/Elements/Avatar'
import { UserFieldSearch } from './UserFieldSearch'
import { UserFieldShow } from './UserFieldShow'
import { UserFieldEdit } from './UserFieldEdit'
import { UserFieldFastSwitch } from './UserFieldFastSwitch'
import BaseDisplayItem, {
    BaseDisplayItemProps,
    BaseDisplayItemState,
} from '../../BaseDisplayItem'

interface User {
    id: string
    username?: string
    label?: React.ReactNode | string
    text?: string
    firstname?: string
    lastname?: string
    profile?: {
        first_name: string
        last_name: string
        preposition?: string
    }
    environment_user_active?: boolean
}

interface UserFieldProps extends BaseDisplayItemProps<User[] | null> {
    item?: any
    isNewDossier?: boolean
    kedo: any
    dossier?: any
}

interface UserFieldState extends BaseDisplayItemState<User[] | null> {
    default_current_user: boolean
    display_name_style: boolean
    options: any[]
    optionsLoading: boolean
    showInactive: boolean
    userLoading: boolean
    page: number
    search: string
    selected: User[]
    total: number
    selectedOptions?: any[]
    value?: any
}

class UserField extends BaseDisplayItem<User[] | null, any, any> {
    state: UserFieldState

    constructor(props: UserFieldProps) {
        super(props)

        const defaultCurrent =
            props.item?.settings?.default_current_user === true &&
            props.mode !== 'search' &&
            props.mode !== 'advanced-search'
        let selected: User[] = []
        if (props.value?.length > 0) {
            selected = props.value.map((v) => {
                if (typeof v === 'string') {
                    return { id: v }
                }
                return v
            })
        } else if (defaultCurrent && props.isNewDossier) {
            const currentUser = {
                id: props.kedo.user().getUserId(),
                username: props.kedo.user().getUsername(),
                label: props.kedo.user().getUsername(),
            }

            selected = [currentUser]
        }

        const displayNameStyle =
            props.item?.settings?.display_name_style === true &&
            props.mode !== 'search' &&
            props.mode !== 'advanced-search'

        this.state = {
            default_current_user: defaultCurrent,
            display_name_style: displayNameStyle,
            options: [],
            optionsLoading: false,
            showInactive: true,
            userLoading: false,
            page: 0,
            search: '',
            selected,
            total: 0,
            selectedOptions: undefined,
            value: this.props.value || [],
        }

        this.onCancel = this.onCancel.bind(this)
        this.getOptions = this.getOptions.bind(this)
        this.getOption = this.getOption.bind(this)
        this.onFastSwitchChange = this.onFastSwitchChange.bind(this)
    }

    override getValue(): User[] | null {
        if (!this.state.selected || this.state.selected.length === 0) {
            return null
        }

        return this.state.selected.map((item) => ({
            id: item.id,
        }))
    }

    onCancel() {
        this.setState({
            selected:
                this.state.value.map((v) => {
                    if (typeof v === 'string') {
                        return { id: v }
                    }
                    return v
                }) || [],
        })
    }

    userDisplay(item: User) {
        if (item && React.isValidElement?.(item.label)) {
            return item.label
        }
        if (this.props.item.settings?.display_name_style === 'avatar') {
            return (
                <Avatar
                    key={item.id || item}
                    kedo={this.props.kedo}
                    user={{
                        userId: item.id,
                        username: item.text ? item?.text : this.getName(item),
                    }}
                    options={{ size: 25, showPresence: false }}
                />
            )
        } else if (this.props.item.settings?.display_name_style === 'both') {
            return (
                <>
                    <Avatar
                        key={item.id || item}
                        kedo={this.props.kedo}
                        user={{
                            userId: item.id,
                            username: item.text
                                ? item?.text
                                : this.getName(item),
                        }}
                        options={{ size: 25, showPresence: false }}
                    />
                    &nbsp;
                    {item.text ? item?.text : this.getName(item)}
                </>
            )
        }

        return item.text ? item?.text : this.getName(item)
    }

    renderShow() {
        if (!this.state.selected || this.state.selected.length <= 0) return null

        if (this.props.mode === 'list' && this.isFastSwitch()) {
            return this.renderFastSwitch()
        }

        return (
            <UserFieldShow
                users={this.state.selected}
                displayStyle={
                    this.props.item.settings?.display_name_style === 'avatar'
                        ? ' avatar '
                        : ''
                }
                userDisplay={(user) => this.userDisplay(user)}
            />
        )
    }

    onFastSwitchChange(items: User | User[]) {
        const isSingleSelect = this.isSingleSelect()
        const api = this.props.kedo.api()
        const dossierId = this.props.dossier?.id
        const curSelected = isSingleSelect
            ? (items as User).id
                ? [(items as User).id]
                : []
            : (items as User[]).map((item) => item.id)

        this.setState({ userLoading: true })
        api.post(
            api.getContentUserEndpoint(dossierId, this.props.item.id),
            curSelected
        )
            .then(() => {
                const newSelected = Array.isArray(items) ? items : [items]
                this.setState({ selected: newSelected, userLoading: false })
                this.updateValue(newSelected)
            })
            .catch((error: any) => {
                console.error('Error updating users:', error)
                this.setState({ userLoading: false })
            })
    }

    addSelectedItem(data: User | User[] | null) {
        // Handle null or undefined data
        if (!data) {
            this.setState({ selected: [] })
            this.updateValue([])
            return
        }

        // Normalize data to an array
        const users: User[] = Array.isArray(data) ? data : [data]

        // Extract values and update state
        const values = users.map((user) => ({ id: user.value }))
        this.setState({ selected: users })
        this.updateValue(values)
    }

    getOptions(inputValue = '', callBack?: (options: any[]) => void) {
        const params: any = {
            params: {
                environment: this.props.kedo.env().getEnvironment().id,
                limit: 25,
            },
        }
        if (inputValue.length > 0) params.params.name = inputValue
        if (!this.state.showInactive) params.params.active = true

        this.setState({ userLoading: true })
        this.props.kedo
            .api()
            .get(this.props.kedo.api().getUserEndpoint(), params)
            .then((response: any) => {
                const newOptions = response.data.results.map((user: User) => {
                    const id = String(user.id)
                    return {
                        id,
                        value: id,
                        key: id,
                        label: (
                            <span
                                style={
                                    !user.environment_user_active
                                        ? { color: 'gray' }
                                        : undefined
                                }
                            >
                                {this.userDisplay({ ...user, id })}
                                {!user.environment_user_active ? (
                                    <FontAwesomeIcon icon={faUserSlash} />
                                ) : null}
                            </span>
                        ),
                    }
                })
                this.setState({ options: newOptions })
                if (callBack) callBack(newOptions)
            })
            .catch((error: any) => {
                console.error('Error fetching options:', error)
                if (callBack) callBack([])
            })
            .finally(() => this.setState({ userLoading: false }))
    }

    async getOption(userId: string) {
        const api = this.props.kedo.api()
        const params = {
            params: { environment: this.props.kedo.env().getEnvironment().id },
        }

        return api
            .getAsync(api.getUserEndpoint(userId), params)
            .then((response: any) => ({
                id: userId,
                value: userId,
                label: (
                    <span>
                        {this.userDisplay({ id: userId, ...response.data })}
                    </span>
                ),
            }))
    }

    async componentDidMount() {
        if (
            (this.props.mode === 'search' ||
                this.props.mode === 'advanced-search') &&
            !this.state.selectedOptions
        ) {
            const tempArray: any[] = []

            for (const item of this.state.selected) {
                let option = this.state.options.find(
                    (opt) => opt.id === item.id
                )
                if (!option) {
                    option = await this.getOption(item.id)
                    this.setState((prevState) => {
                        if (
                            !prevState.options.some(
                                (existingOption) =>
                                    existingOption.id === option.id
                            )
                        ) {
                            return { options: [...prevState.options, option] }
                        }
                        return null
                    })
                }
                tempArray.push(option)
            }

            if (Array.isArray(tempArray)) {
                this.setState({
                    selected: tempArray,
                    selectedOptions: tempArray,
                })
            }
        }
    }

    componentDidUpdate(prevProps: BaseDisplayItemProps<User[] | null, any>) {
        const prev = prevProps as UserFieldProps
        if (prevProps.value !== this.props.value) {
            const selected =
                this.props.value?.map((v) => {
                    if (typeof v === 'string') {
                        return { id: v }
                    }
                    return v
                }) || []

            this.setState({ selected, value: this.props.value || [] })
        }
        if (prev.mode !== 'list' && this.props.mode === 'list') {
            this.getOptions('', (options) => this.setState({ options }))
        }
    }

    isSingleSelect() {
        const settings =
            this.props.item.def_dossier_def_field?.def_field.settings
        return !(settings?.multiple === true)
    }

    isFastSwitch() {
        if (!this.props.item || !this.props.item.settings) {
            return false
        }
        if (this.getSetting('fast_switch') === true) {
            return true
        }

        return false
    }

    getName(item: User) {
        if (item.text) return item.text
        if (item.label) return item.label as string
        if (!item.profile) return `${item.lastname}, ${item.firstname}`
        return (
            item.profile.last_name +
            ', ' +
            item.profile.first_name +
            (item.profile.preposition ? ' ' + item.profile.preposition : '')
        )
    }

    handleSearch = (value: string) => {
        this.getOptions(value)
    }

    renderEdit() {
        return (
            <>
                <UserFieldEdit
                    ref={this.innerRef}
                    selected={this.state.selected}
                    options={this.state.options}
                    isSingleSelect={this.isSingleSelect()}
                    userLoading={this.state.userLoading}
                    errors={this.props.errors?.[0]}
                    item={this.props.item}
                    kedo={this.props.kedo}
                    userDisplay={(user) => this.userDisplay(user)}
                    handleSearch={this.handleSearch}
                    getOptions={this.getOptions}
                    addSelectedItem={(items) => this.addSelectedItem(items)}
                />
                {this.isInvalid() ? (
                    <span className="fake-feedback">{this.getErrorText()}</span>
                ) : null}
            </>
        )
    }

    renderSearch() {
        if (!this.state.selectedOptions) return null

        return (
            <UserFieldSearch
                ref={this.innerRef}
                selected={this.state.selected}
                options={this.state.options}
                userLoading={this.state.userLoading}
                itemId={this.props.item.id}
                placeholder={this.props.kedo.t('Search') + '...'}
                errors={this.props.errors}
                onSearch={this.handleSearch}
                onChange={(selected) => this.addSelectedItem(selected)}
                getOptions={(callback) => this.getOptions('', callback)}
            />
        )
    }

    renderFastSwitch() {
        return (
            <UserFieldFastSwitch
                ref={this.innerRef}
                selected={this.state.selected}
                options={this.state.options}
                isSingleSelect={this.isSingleSelect()}
                isInvalid={this.isInvalid()}
                userLoading={this.state.userLoading}
                itemId={this.props.item.id}
                placeholder={this.props.kedo.t('Search') + '...'}
                errors={this.props.errors}
                userDisplay={(user) => this.userDisplay(user)}
                onSearch={this.handleSearch}
                onClickLoad={(cb) =>
                    this.getOptions('', (options) => this.setState({ options }))
                }
                onFastSwitchChange={this.onFastSwitchChange}
            />
        )
    }
}

export default UserField
